// src/EditPartnerModal.js
import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import {
    Modal, Box, Typography, TextField, Button, Select, MenuItem, FormControl,
    InputLabel, FormGroup, FormControlLabel, Checkbox, Grid, Alert, CircularProgress
} from '@mui/material';
import MaskedInput from './MaskedInput';

const ZONAS = [
    "Saúde", "Morumbi", "Aclimação", "Ipiranga", "Mooca", "Carrão", 
    "Itaquera", "Guarulhos", "Santo André", "São Bernardo do Campo", 
    "São Caetano do Sul", "Litoral"
];

const style = { 
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
    width: { xs: '90%', sm: 700 }, // Largura responsiva
    bgcolor: 'background.paper', boxShadow: 24, p: 4, maxHeight: '90vh', overflowY: 'auto' 
};

function EditPartnerModal({ user, partner, onClose, onPartnerUpdated }) {
    const [formData, setFormData] = useState({ ...partner });
    const [gerentes, setGerentes] = useState([]);
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setFormData({ ...partner });
        const fetchGerentes = async () => {
            const q = query(collection(db, "usuarios"), where("tipoUsuario", "==", "gerente"));
            const snapshot = await getDocs(q);
            setGerentes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        };
        fetchGerentes();
    }, [partner]);

    const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    const handleZoneChange = (e) => {
        const { value, checked } = e.target;
        const currentZones = formData.zonaAtuacao || [];
        const newZones = checked ? [...currentZones, value] : currentZones.filter(z => z !== value);
        setFormData(p => ({ ...p, zonaAtuacao: newZones }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErro('');
        setSalvando(true);
        try {
            const { id, ...dataToUpdate } = formData;
            if (!dataToUpdate.gerenteId) dataToUpdate.gerenteId = null;
            const finalData = { ...dataToUpdate, _updatedBy: { uid: user.uid, email: user.email, at: new Date() } };
            await updateDoc(doc(db, "usuarios", id), finalData);
            onPartnerUpdated();
        } catch (err) {
            console.error(err);
            if (err.code === 'permission-denied') {
                setErro('Sem permissão para editar este parceiro. Verifique se ele está vinculado a você.');
            } else {
                setErro('Erro ao salvar: ' + err.message);
            }
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal open={true} onClose={onClose}>
            <Box sx={style}>
                <Typography variant="h6">Editar Parceiro</Typography>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        {/* Linha 1 */}
                        <Grid item xs={12} sm={6}><TextField fullWidth name="nome" label="Nome / Razão Social" value={formData.nome || ''} onChange={handleChange} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth name="telefone" label="Telefone / WhatsApp" value={formData.telefone || ''} onChange={handleChange} InputProps={{ inputComponent: MaskedInput, inputProps: { mask: '(00) 00000-0000' } }} /></Grid>

                        {/* Linha 2 - Documentos */}
                        {formData.tipoUsuario === 'imobiliaria' && <Grid item xs={12} sm={6}><TextField fullWidth name="cnpj" label="CNPJ (Opcional)" value={formData.cnpj || ''} onChange={handleChange} InputProps={{ inputComponent: MaskedInput, inputProps: { mask: '00.000.000/0000-00' } }} /></Grid>}
                        {formData.tipoUsuario === 'autonomo' && <><Grid item xs={12} sm={6}><TextField fullWidth name="cpf" label="CPF (Opcional)" value={formData.cpf || ''} onChange={handleChange} InputProps={{ inputComponent: MaskedInput, inputProps: { mask: '000.000.000-00' } }} /></Grid><Grid item xs={12} sm={6}><TextField fullWidth name="rg" label="RG (Opcional)" value={formData.rg || ''} onChange={handleChange} InputProps={{ inputComponent: MaskedInput, inputProps: { mask: '00.000.000-0' } }} /></Grid></>}
                        
                        {/* Linha 3 */}
                        <Grid item xs={12}><TextField fullWidth name="endereco" label="Endereço (Opcional)" value={formData.endereco || ''} onChange={handleChange} /></Grid>
                        
                        {/* Linha 4 */}
                        <Grid item xs={12} sm={4}><TextField fullWidth name="creci" label="CRECI" value={formData.creci || ''} onChange={handleChange} required /></Grid>
                        <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Status</InputLabel><Select name="status" value={formData.status} label="Status" onChange={handleChange}><MenuItem value="ativo">Ativo</MenuItem><MenuItem value="inativo">Inativo</MenuItem></Select></FormControl></Grid>
                        <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Gerente</InputLabel><Select name="gerenteId" value={formData.gerenteId || ''} label="Gerente" onChange={handleChange}><MenuItem value="">-- Nenhum --</MenuItem>{gerentes.map(g => <MenuItem key={g.id} value={g.id}>{g.nome}</MenuItem>)}</Select></FormControl></Grid>
                        
                        {/* Linha 5 - Zonas */}
                        <Grid item xs={12}><FormControl component="fieldset" variant="standard"><Typography component="legend">Zona de Atuação:</Typography><FormGroup row>{ZONAS.map(zona => <FormControlLabel key={zona} control={<Checkbox checked={formData.zonaAtuacao?.includes(zona)} onChange={handleZoneChange} value={zona} />} label={zona} />)}</FormGroup></FormControl></Grid>
                        
                        {/* Linha 6 - Observações (A Correção Definitiva) */}
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={4} name="observacoesInternas" label="Observações Internas" value={formData.observacoesInternas || ''} onChange={handleChange} />
                        </Grid>
                    </Grid>
                    
                    {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={onClose} sx={{ mr: 1 }} disabled={salvando}>Cancelar</Button>
                        <Button type="submit" variant="contained" disabled={salvando}>
                            {salvando ? <><CircularProgress size={16} sx={{ mr: 1 }} />Salvando...</> : 'Salvar Alterações'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}
export default EditPartnerModal;